import * as React from 'react'
import { useSession } from 'next-auth/react'
import Link from 'next/link'

import { useTheme } from '@mui/material'
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded'
import Accordion from '@mui/material/Accordion'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'

import { I18n, useTranslation } from 'components/hooks/use-translation'
import { useUser } from 'components/hooks/state'
import SupportIcon from 'components/atomic/molecules/support-icon'
import IconButton from 'components/atomic/atoms/icon-button'
import Button from 'components/atomic/atoms/button'
import Avatar from 'components/atomic/atoms/avatar'
import Text from 'components/atomic/atoms/text'
import Logo from 'components/atomic/atoms/logo'

import Routes from 'utils/routes'

import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded'
import CatalogDropdown from './catalog-dropdown'
import WikiDropdown from './wiki-dropdown'

import { HeaderBarHeightPixel } from '../..'
import useDebounce from 'components/hooks/use-debounce'

const AuthSection = () => {
    const { t }: I18n = useTranslation()
    const { user } = useUser()

    const isFreeUser = React.useMemo(() => (!user ? false : user.subscription_status !== 'ACTIVE'), [user])
    const hasDoneQuiz = React.useMemo(() => (!user ? false : !!user.personalized_path), [user])

    return (
        <Stack direction="row" spacing={1} alignItems="center">
            {hasDoneQuiz ? (
                <Link href={isFreeUser ? Routes.SortingQuizResults : Routes.MyPath} passHref>
                    <a>
                        <Button variant="outlined">{t('common:header.myPath')}</Button>
                    </a>
                </Link>
            ) : (
                <Link href={Routes.SortingQuiz} passHref>
                    <a>
                        <Button variant="outlined">{t('common:header.takeSortingQuiz')}</Button>
                    </a>
                </Link>
            )}

            {isFreeUser ? (
                <Link href={Routes.Pricing} passHref>
                    <a>
                        <Button variant="contained">{t('common:header.upgrade')}</Button>
                    </a>
                </Link>
            ) : null}

            <Box sx={{ height: '32px' }}>
                <Divider orientation="vertical" />
            </Box>

            <SupportIcon />

            <Link href={Routes.AccountSettings} passHref>
                <a>
                    <IconButton aria-label="Account settings" variant="outlined" tooltip={t('common:header.settings')}>
                        <SettingsRoundedIcon fontSize="small" />
                    </IconButton>
                </a>
            </Link>

            <Link href={Routes.AccountSettings} passHref>
                <a>
                    <Button
                        color="inherit"
                        endIcon={
                            <Badge
                                overlap="circular"
                                color="success"
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                variant="dot">
                                <Avatar
                                    alt=""
                                    src={user?.avatar || undefined}
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        border: (theme) => `1px solid ${theme.palette.divider}`,
                                    }}
                                />
                            </Badge>
                        }>
                        <Stack direction="column">
                            <Text variant="body2" sx={{ lineHeight: 1.3 }}>
                                {user?.first_name}
                            </Text>
                            <Text
                                variant="caption"
                                textAlign="right"
                                color={isFreeUser ? 'text.secondary' : 'gold.main'}
                                sx={{ lineHeight: 1.3 }}>
                                {isFreeUser ? 'Starter' : 'PRO'}
                            </Text>
                        </Stack>
                    </Button>
                </a>
            </Link>
        </Stack>
    )
}

const UnAuthSection = () => {
    const { t }: I18n = useTranslation()

    return (
        <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ display: { sm: 'none', md: 'block' } }}>
                <SupportIcon />
            </Box>

            <Box sx={{ display: { sm: 'none', md: 'block' } }}>
                <Link href={Routes.SortingQuiz}>
                    <a>
                        <Button variant="outlined">{t('common:header.takeSortingQuiz')}</Button>
                    </a>
                </Link>
            </Box>

            <Box sx={{ display: { sm: 'none', md: 'block' }, height: '32px' }}>
                <Divider orientation="vertical" />
            </Box>

            <Link href={Routes.SignUp}>
                <a>
                    <Button variant="contained">{t('common:header.signup')}</Button>
                </a>
            </Link>
            <Link href={Routes.SignIn}>
                <a>
                    <Button variant="text">{t('common:header.login')}</Button>
                </a>
            </Link>
        </Stack>
    )
}

const DesktopHeader = (props: { drawerOpen: boolean; onDrawerToggle: (a: boolean) => void }) => {
    const [action, setAction] = React.useState<{ content: 'CATALOG' | 'WIKI'; visible: boolean }>({
        content: 'CATALOG',
        visible: false,
    })

    const [debouncedContent] = useDebounce(action, 100)

    React.useEffect(() => {
        props.onDrawerToggle(debouncedContent.visible)
    }, [debouncedContent, props])

    const { status } = useSession()
    const theme = useTheme()

    return (
        <>
            <Accordion
                expanded={props.drawerOpen}
                TransitionProps={{ timeout: 200 }}
                sx={{
                    width: '100%',
                    zIndex: 'inherit',
                    background: theme.palette.background.default,
                }}
                elevation={0}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent={'space-between'}
                    sx={{ minHeight: HeaderBarHeightPixel }}>
                    <Stack direction="row" spacing={4} alignItems="center">
                        <Logo sx={{ flexGrow: 1 }} />
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Button
                                color={props.drawerOpen && action.content === 'CATALOG' ? 'primary' : 'inherit'}
                                onClick={() =>
                                    setAction((prev) => ({
                                        content: 'CATALOG',
                                        visible: !prev.visible || action.content !== 'CATALOG',
                                    }))
                                }
                                // onClick={() => setAction({ content: 'CATALOG', visible: !action.visible })}
                                // onMouseEnter={() => setAction({ content: 'CATALOG', visible: true })}
                                // onMouseLeave={() => setAction({ content: 'CATALOG', visible: false })}
                                endIcon={
                                    props.drawerOpen && action.content === 'CATALOG' ? (
                                        <ExpandLessRoundedIcon sx={{ marginLeft: '-4px' }} />
                                    ) : (
                                        <ExpandMoreRoundedIcon sx={{ marginLeft: '-4px' }} />
                                    )
                                }>
                                Catalogo
                            </Button>
                            <Button
                                color={props.drawerOpen && action.content === 'WIKI' ? 'primary' : 'inherit'}
                                onClick={() =>
                                    setAction((prev) => ({
                                        content: 'WIKI',
                                        visible: !prev.visible || action.content !== 'WIKI',
                                    }))
                                }
                                // onClick={() => setAction({ content: 'WIKI', visible: !action.visible })}
                                // onMouseEnter={() => setAction({ content: 'WIKI', visible: true })}
                                // onMouseLeave={() => setAction({ content: 'WIKI', visible: false })}
                                endIcon={
                                    props.drawerOpen && action.content === 'WIKI' ? (
                                        <ExpandLessRoundedIcon sx={{ marginLeft: '-4px' }} />
                                    ) : (
                                        <ExpandMoreRoundedIcon sx={{ marginLeft: '-4px' }} />
                                    )
                                }>
                                Wiki
                            </Button>
                        </Stack>
                    </Stack>

                    {status === 'authenticated' ? <AuthSection /> : <UnAuthSection />}
                </Stack>

                {action.content === 'CATALOG' ? (
                    <CatalogDropdown onHover={(visible) => setAction({ content: 'CATALOG', visible })} />
                ) : (
                    <WikiDropdown onHover={(visible) => setAction({ content: 'WIKI', visible })} />
                )}
            </Accordion>
        </>
    )
}

export default DesktopHeader
